import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import intersectionWith from 'lodash/intersectionWith';
import upperFirst from 'lodash/upperFirst';
import {
  SEARCH_FILTERS_MAP
} from 'utils/constants/productDetailsPage';

// Filter options come from the state. Either string value or array of 2 integers.
// Convert this into designed display value
export const convertFilterOptionsToDisplay = (filter, options, rangedFilters, selectedOptions) => {
  let convertedOptions;

  if (options.length === 0) { return []; }

  if (rangedFilters.includes(filter) && !options[0].param) {
    let label;
    let currencySymbol = '';
    let dimensionSymbol = '';
    if (filter.toLowerCase() === 'price') {
      currencySymbol = '$';
    } else {
      dimensionSymbol = '"';
    }

    convertedOptions = options.map(element => {
      let option = element.name || element;

      if (option[0] === 0 && option[1] === -1) {
        // only one option for all products
        label = `All ${upperFirst(filter)}s`;
      } else if (option[0] === 0) {
        // handle min ranged option
        label = `Under ${currencySymbol}${option[1]}${dimensionSymbol}`;
      } else if (option[1] === -1) {
        // handle max ranged option
        label = `Above ${currencySymbol}${option[0]}${dimensionSymbol}`;
      } else {
        label = `${currencySymbol}${option[0]}-${option[1]}${dimensionSymbol}`;
      }
      return { value: option, label };
    });
  } else {
    convertedOptions = options.map(option => (
      { value: option.name || option, label: option.name || option, param: option.param }
    ));
  }

  // check if any filters are selected
  if (selectedOptions && selectedOptions.length > 0) {
    // filters need a clear all option
    convertedOptions.push({ value: 'clearAllPlaceholder', label: 'Clear All' });
  }

  return convertedOptions;
};

// determine if the selected option has already been selected.
// this is necessary to show proper checkbox state for filter options
// dropdown
export const isOptionSelected = (filter, filterValues, rangedFilters, selectedOption) => {
  if (rangedFilters.includes(filter) && !selectedOption.param) {
    return filterValues.some((arrValue) => (
      arrValue.value.every((value, idx) => value === selectedOption.value[idx])
    ));
  } else {
    if (filterValues.some) {
      return filterValues.some(
        option => {
          return selectedOption.value === option.value;
        }
      );
    } else {
      return filterValues.value === selectedOption.value;
    }
  }
};

export const isCategoryOptionSelected = (value, selectedOption) => {
  // an option is selected if it is in filterValues, or its child is, or any sub children

  // recurse down the chain until we find an option with no children
  // at each level check for the option
  // if we reach the end and don't find it return false
  // if we find it stop recursing and return true
  if (!value) { return false; }

  if (value.value === selectedOption.value) { return true; }

  if (value.children) { return isCategoryOptionSelected(value.children, selectedOption); }

  return false;
};

// create the data necessary to make post request to
// product search api (currently warehouse/api/spaces/products/search)
export const createFilterData = (filter, filterValues, rangedFilters) => {
  const data = { name: SEARCH_FILTERS_MAP[filter] || filter };
  if (rangedFilters.includes(filter)) {
    data.value = getMinMaxOfRangeOptions(filterValues);
  } else {
    data.value = filterValues.map(option => option.value);
  }

  return data;
};

// Determines if any filters are being applied
// main use case to toggle clear all filters button on/off
export const countFiltersApplied = (filterValues) => {
  let appliedFilters = 0;
  Object.keys(filterValues).forEach(filter => {
    const selectedOptions = filterValues[filter];
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) appliedFilters++;
    } else {
      if (selectedOptions.value) appliedFilters++;
    }
  });

  return appliedFilters;
};

export const getMinMaxOfRangeOptions = (filterValues) => {
  const optionValues = filterValues.reduce((a, option) => {
    return a.concat(option.value);
  }, []);

  const max = optionValues.includes(-1) ? -1 : Math.max(...optionValues);
  const min = Math.min(...optionValues.filter(option => option >= 0));

  return [min, max];
};

const getDiffOfFilterArrays = (newOptions, oldOptions) => {
  return differenceWith(newOptions, oldOptions, isEqual);
};

const checkIfRangeJoined = (options) => {
  const sortedOptions = options.sort((a, b) => a.value[0] - b.value[0]);
  for (let i = 0; i < sortedOptions.length - 1; i++) {
    if (sortedOptions[i].value[1] !== sortedOptions[i + 1].value[0]) return false;
  }
  return true;
};

export const generateNewRangedOptions = (newOptions, oldOptions) => {
  if (newOptions.length > oldOptions.length) {
    const currentRange = getMinMaxOfRangeOptions(oldOptions);
    const newOption = getDiffOfFilterArrays(newOptions, oldOptions)[0];
    if (intersectionWith(currentRange, newOption.value).length === 0) return [newOption];
  } else {
    if (!checkIfRangeJoined(newOptions)) return [newOptions[0]];
  }
  return null;
};
